import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { useScript } from '../context/ScriptContext';
import { useAuth } from '../context/AuthContext';
import { NewsItem } from '../types';
import * as api from '../services/api';
import { ApiError } from '../services/api';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useLangFade } from '../hooks/useLangFade';

const SingleNewsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useScript();
  const langFade = useLangFade();
  const { isAdmin } = useAuth();

  // Where to go on "← Назад": the page the user came from (passed via router
  // state), falling back to /novosti when arriving directly (typed URL, refresh,
  // external link).
  const backTo = (location.state as { from?: string } | null)?.from || '/novosti';
  const backLabel = t('← Назад');
  const [news, setNews] = useState<NewsItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    setNotFound(false);
    setNews(null);
    api.getNewsById(Number(id))
      .then(item => { setNews(item); setLoading(false); })
      .catch((e: ApiError) => {
        if (e?.status === 404) {
          setNotFound(true);
        } else {
          setError(e?.message || 'Грешка при учитавању.');
        }
        setLoading(false);
      });
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Tab title reflects the loaded news item; falls back to a neutral title
  // while loading and a 404 title when the item is missing. Titles are kept
  // in Cyrillic regardless of the user's script toggle (see useDocumentTitle).
  const pageTitle = notFound
    ? 'Вест није пронађена — Манастир Градац'
    : news
      ? `${news.title} — Манастир Градац`
      : 'Манастир Градац';
  useDocumentTitle(pageTitle, id ? `/novosti/${id}` : undefined);

  if (loading) {
    return (
      <div className="news-page">
        <div className="state-block" role="status" aria-live="polite" style={{ paddingTop: '80px' }}>
          <div className="state-spinner" aria-hidden="true" />
          <span>{t('Учитавање...')}</span>
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="news-page" style={{ textAlign: 'center', paddingTop: '60px' }}>
        <p>{t('Вест није пронађена.')}</p>
        <Link to={backTo} className="back-link">{backLabel}</Link>
      </div>
    );
  }

  if (error || !news) {
    return (
      <div className="news-page">
        <div className="state-block state-error" role="alert" style={{ paddingTop: '80px' }}>
          <span>{t(error || 'Грешка при учитавању.')}</span>
          <button type="button" className="state-retry" onClick={load}>
            {t('Покушај поново')}
          </button>
          <Link to={backTo} className="back-link" style={{ marginTop: '8px' }}>
            {backLabel}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="news-page">
      <figure
        className="single-news-frame"
        style={{ backgroundImage: `url(${news.imageUrl})` }}
        role="img"
        aria-label={t(news.title)}
      />
      <article className={`single-news-card ${langFade}`}>
        <div className="single-news-meta">
          <span className="news-date-inline">{t(news.dateLabel)}</span>
          {isAdmin && (
            <button className="post-edit-btn" onClick={() => navigate(`/novosti/${id}/uredi`)}>
              {t('Уреди вест ✎')}
            </button>
          )}
        </div>
        <h1 className="single-news-title">{t(news.title)}</h1>
        <p className="single-news-excerpt">{t(news.excerpt)}</p>
        <div className="single-news-divider" aria-hidden="true">☦</div>
        <div className="single-news-content">{t(news.content)}</div>
        <div className="single-news-footer">
          <Link to={backTo} className="back-link">{backLabel}</Link>
        </div>
      </article>
    </div>
  );
};

export default SingleNewsPage;
